/* ----------------- React --------------- */
import { useState } from "react";

/* ----------------- Child components --------------- */
import AccountSettingsApplet from "@memorang/ui/src/components/AccountSettingsApplet";
import AppSelectionDialog from "../components/dialogs/AppSelectionDialog";
import DeleteConfirmationDialog from "../components/dialogs/DeleteConfirmationDialog";
import DeleteFeedbackDialog from "../components/dialogs/DeleteFeedbackDialog";
import { SignoutDialog } from "../components/dialogs/SignOutDialog";

import { useThemeContext } from "@contexts/ThemeContext";
import { useDevice } from "@hooks/useDevice";
/* ----------------- Hooks --------------- */
import useAccountSettings from "../hooks/useAccountSettings";
import { useDelete } from "../hooks/useDelete";

import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import FaqsDialog from "@features/faqs/components/FaqsDialog";
import { buildType } from "@helpers/expo-extras";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
/* ----------------- UI Libraries --------------- */

import { cacheKeys } from "@constants/cache-keys";
import { pageViewTypes } from "@constants/tracking";
import { handleNavigateBack } from "@helpers/common";
import useAnalytics from "@hooks/useAnalytics";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import type { ExamDetails } from "@memorang/types/src/exams";
import { useQueryClient } from "@tanstack/react-query";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import SetExamDateContainer from "components/SetExamDateContainer";
import { useTranslation } from "react-i18next";
import type { Apps } from "types/apps";
import LanguageDialog from "../components/dialogs/LanguageDialog";

const AccountSettings = () => {
	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showSignOutDialog, setShowSignOutDialog] = useState(false);
	const [showLanguageDialog, setShowLanguageDialog] = useState(false);
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [showFaqsDialog, setShowFaqsDialog] = useState(false);
	const [showAppSelectionDialog, setShowAppSelectionDialog] = useState(false);
	const {
		toggleShowDeleteConfirmationDialog,
		toggleShowDeleteFeedbackDialog,
		showDeleteConfirmationDialog,
		showDeleteFeedbackDialog,
		selectedReasons,
		feedback,
		handleSetFeedback,
		handleSendFeedback,
		checkboxStates,
		handleChangeCheckboxState,
	} = useDelete();

	const queryClient = useQueryClient();
	const exams = queryClient.getQueryData<ExamDetails[]>([cacheKeys.exams]);
	const hasMoreThanOneExam = exams && exams.length > 1;
	const showAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const selectedApp = useGlobalStore((state) => state.selectedApp);

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const toggleShowFaqsDialog = () => {
		setShowFaqsDialog((prev) => !prev);
	};
	const toggleShowSignOutDialog = () => {
		setShowSignOutDialog((prev) => !prev);
	};
	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};
	const { handleExamsFilterNavigation } = useCustomNavigation();
	const handleNavigateToExamsRoute = () => {
		handleExamsFilterNavigation(toggleShowExamsFilterDialog);
	};
	const toggleShowAppSelectionDialog = () => {
		setShowAppSelectionDialog((prev) => !prev);
	};
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	useAnalytics({
		type: pageViewTypes.settings,
	});
	const { isDarkMode, toggleDarkMode } = useThemeContext();
	const appFont = useAppStore((store) => store.app.font);
	const toggleAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.toggleAudioPlaybackTestDialog,
	);
	const { getAccountSections } = useAccountSettings({
		toggleShowDatePicker,
		toggleShowSignOutDialog,
		toggleDarkMode,
		toggleShowDeleteFeedbackDialog,
		toggleLanguageDialog: () => setShowLanguageDialog((prev) => !prev),
		toggleShowPurchaseDialog,
		toggleShowFaqsDialog,
		toggleAudioPlaybackTestDialog,
		toggleShowAppSelectionDialog,
	});
	const sections = getAccountSections();

	return (
		<>
			<AccountSettingsApplet
				sections={sections}
				font={appFont}
				isDarkMode={isDarkMode}
				showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
				accountExtendValidity={t("account.extendValidity")}
				hasMoreThanOneExam={hasMoreThanOneExam}
				toggleDarkMode={toggleDarkMode}
				toggleAudioPlaybackTestDialog={toggleAudioPlaybackTestDialog}
				handleNavigateToExamsRoute={handleNavigateToExamsRoute}
				customAppBar={{
					options: {
						headerTitle: t("account.title"),
						subtitle: isMobile ? "" : t("account.subtitle"),
						headerRight: () => null,
					},
					back: true,
					onBackPress: () => {
						handleNavigateBack("/(protected)/(tabs)/home");
					},
				}}
			/>
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showSignOutDialog && (
				<SignoutDialog
					visible={showSignOutDialog}
					handleClose={toggleShowSignOutDialog}
				/>
			)}
			{showDeleteFeedbackDialog && (
				<DeleteFeedbackDialog
					visible={showDeleteFeedbackDialog}
					handleDismiss={toggleShowDeleteFeedbackDialog}
					handleShowDeleteConfirmation={toggleShowDeleteConfirmationDialog}
					feedback={feedback}
					handleSetFeedback={handleSetFeedback}
					handleSelectOption={handleChangeCheckboxState}
					checkboxStates={checkboxStates}
					selectedReasons={selectedReasons}
				/>
			)}
			{showDeleteConfirmationDialog && (
				<DeleteConfirmationDialog
					visible={showDeleteConfirmationDialog}
					handleDismiss={toggleShowDeleteConfirmationDialog}
					handleSendFeedback={handleSendFeedback}
				/>
			)}
			{showLanguageDialog && (
				<LanguageDialog
					visible={showLanguageDialog}
					handleClose={() => setShowLanguageDialog(false)}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{showFaqsDialog && (
				<FaqsDialog
					visible={showFaqsDialog}
					handleClose={toggleShowFaqsDialog}
				/>
			)}
			{showAppSelectionDialog && (
				<AppSelectionDialog
					visible={showAppSelectionDialog}
					handleClose={toggleShowAppSelectionDialog}
					currentApp={selectedApp || (buildType as Apps)}
				/>
			)}
		</>
	);
};

export default AccountSettings;
